import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, AlertCircle, CheckCircle2, Loader2, Heart, Activity, Wind, Info } from 'lucide-react';
import { FHVitalsService, mapSDKResultToWE360 } from '@/services/fhvitals';


// Variable global para serializar inicializaciones y evitar colisión de cámara/recursos
let activeCleanupPromise = Promise.resolve();

/**
 * Modal de medición de signos vitales con cámara usando FHVitals SDK
 */
export default function VitalsMeasurement({ isOpen, onClose, onComplete, userInfo }) {
  const [status, setStatus] = useState('initializing'); // initializing, ready, measuring, complete, error
  const [progress, setProgress] = useState(0);
  const [scanning_status, setScanningStatus] = useState('');
  const [currentResults, setCurrentResults] = useState(null);
  const [finalResults, setFinalResults] = useState(null);
  const [error, setError] = useState(null);
  const [retakeCount, setRetakeCount] = useState(0);
  
  const canvasRef = useRef(null);
  const serviceRef = useRef(null);
  const restartAttemptsRef = useRef(0);
  const restartTimerRef = useRef(null);
  const cameraReadyTimerRef = useRef(null);

  // Target frame count para completar medición (1500 frames ≈ 50s @ 30fps)
  const TARGET_FRAMES = 1500;
  // Máximo de reintentos automáticos ante _restart_ (ABNORMAL_RESULT, FACE_LOSS, MAX_MEASURE_TIME)
  const MAX_AUTO_RESTARTS = 2;

  const clearPendingTimers = () => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    if (cameraReadyTimerRef.current) {
      clearTimeout(cameraReadyTimerRef.current);
      cameraReadyTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    let active = true;
    console.log('[VitalsMeasurement] 🎬 Modal abierto, iniciando setup...', { userInfo });

    const service = new FHVitalsService();
    serviceRef.current = service;

    const initSDK = async () => {
      try {
        clearPendingTimers();
        restartAttemptsRef.current = 0;
        setStatus('initializing');
        setError(null);
        setProgress(0);
        setCurrentResults(null);
        setFinalResults(null);

        console.log('[VitalsMeasurement] ⏳ Esperando que finalicen limpiezas previas...');
        await activeCleanupPromise;

        if (!active) {
          console.log('[VitalsMeasurement] 🚫 Cancelado por desmontaje previo a iniciar setup');
          return;
        }

        console.log('[VitalsMeasurement] 🔍 Verificando SDK disponible...');

        // Verificar que el SDK esté disponible con reintentos
        // NOTA: El SDK se exporta como window.FHVitals
        let retries = 0;
        const maxRetries = 10; // 10 intentos = 5 segundos
        
        while (typeof window.FHVitals === 'undefined' && retries < maxRetries) {
          if (!active) return;
          console.log(`[VitalsMeasurement] ⏳ SDK no encontrado, esperando... (intento ${retries + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 500));
          retries++;
        }
        
        if (!active) return;
        if (typeof window.FHVitals === 'undefined') {
          console.error('[VitalsMeasurement] ❌ SDK no disponible después de espera');
          throw new Error('SDK no disponible. Por favor recarga la página.');
        }

        console.log('[VitalsMeasurement] ✅ SDK disponible, inicializando...');

        // Inicializar SDK — con auto-retry para ERROR_ASSETS_FOLDER_LOAD_FAILED
        // Este error ocurre la primera vez porque el Worker/WASM aún se está descargando.
        // En reintentos posteriores, el navegador ya tiene los archivos en caché.
        const MAX_INIT_RETRIES = 3;
        let initResult;
        for (let attempt = 1; attempt <= MAX_INIT_RETRIES; attempt++) {
          if (!active) return;
          if (attempt > 1) {
            console.log(`[VitalsMeasurement] 🔄 Reintentando init (${attempt}/${MAX_INIT_RETRIES})...`);
            setError(`Cargando SDK... (intento ${attempt}/${MAX_INIT_RETRIES})`);
            // Esperar más en cada intento para dar tiempo al Worker de cargar
            await new Promise(resolve => setTimeout(resolve, attempt * 2000));
            if (!active) return;
            // Crear nueva instancia del servicio para el reintento
            const newService = new FHVitalsService();
            serviceRef.current = newService;
          }
          initResult = await serviceRef.current.init({
            onResult: handleResult,
            onEvent: handleEvent
          });
          const isAssetError = initResult.error === "ERROR_ASSETS_FOLDER_LOAD_FAILED";
          if (!isAssetError) break;
          console.warn(`[VitalsMeasurement] ⚠️ ERROR_ASSETS_FOLDER_LOAD_FAILED en intento ${attempt}`);
        }

        if (!active) return;

        // El SDK puede retornar error como string "ERROR_NONE" o número 0
        const initSuccess = initResult.error === 0 || initResult.error === "ERROR_NONE";
        
        if (!initSuccess) {
          console.error('[VitalsMeasurement] ❌ Error en init:', initResult);
          
          // Si el error es que ya está inicializado, intentar forzar reinicio
          if (initResult.error === "ERROR_HAS_INITIALIZED") {
            console.warn('[VitalsMeasurement] ⚠️ SDK ya inicializado, intentando continuar con preview...');
            // No lanzar error, intentar continuar
          } else {
            throw new Error(`Error al inicializar SDK: código ${initResult.error}`);
          }
        }
        
        // Limpiar mensaje de error temporal si el reintento fue exitoso
        setError(null);

        console.log('[VitalsMeasurement] ✅ SDK inicializado, iniciando preview...');

        // Iniciar preview de cámara
        const previewResult = await serviceRef.current.startPreview('vitals-canvas', 'user');
        
        if (!active) {
          console.log('[VitalsMeasurement] 🧹 Limpiando preview cancelado por desmontaje...');
          serviceRef.current.cleanup().catch(() => {});
          return;
        }

        const previewSuccess = previewResult.error === 0 || previewResult.error === "ERROR_NONE";
        if (!previewSuccess) {
          console.error('[VitalsMeasurement] ❌ Error en preview:', previewResult);
          throw new Error(`Error al iniciar cámara: código ${previewResult.error}`);
        }

        console.log('[VitalsMeasurement] ✅ Preview iniciado correctamente');

      } catch (err) {
        if (!active) return;
        console.error('[VitalsMeasurement] ❌ Error durante setup:', err);
        setError(err.message || 'Error desconocido al inicializar');
        setStatus('error');
      }
    };

    initSDK();

    return () => {
      active = false;
      clearPendingTimers();
      console.log('[VitalsMeasurement] 🧹 Limpiando recursos al cerrar modal...');
      if (serviceRef.current) {
        const currentService = serviceRef.current;
        activeCleanupPromise = currentService.cleanup().catch(err => {
          console.error('[VitalsMeasurement] Error en cleanup:', err);
        });
        serviceRef.current = null;
      }
    };
  }, [isOpen, retakeCount]);

  const handleResult = (result) => {
    // Log solo en milestones importantes
    if (result.frame_id === 1) {
      console.log('[VitalsMeasurement] 🎯 Primera medición recibida');
      setError(null); // Limpiar advertencias de reinicio previo al comenzar el nuevo ciclo
    }
    
    // Actualizar progreso
    const progress_pct = Math.min(100, Math.round((result.frame_id / TARGET_FRAMES) * 100));
    setProgress(progress_pct);

    // Actualizar estado de scanning
    if (result.scanning_status) {
      console.log('[VitalsMeasurement] ⚠️ Scanning status:', result.scanning_status);
      setScanningStatus(result.scanning_status);
    } else {
      setScanningStatus('');
    }

    // Guardar resultados actuales Y actualizar UI en tiempo real
    setCurrentResults(result);

    // Verificar si completó la medición
    if (result.frame_id >= TARGET_FRAMES) {
      console.log('[VitalsMeasurement] 🎉 Medición completada!', result);
      handleMeasurementComplete(result);
    }
  };

  const handleEvent = (event) => {
    console.log('[VitalsMeasurement] 📡 Evento recibido:', event);

    if (event.state === '_camera_ready_') {
      // Dar 3s al modelo WASM para compilar completamente (especialmente en primera carga)
      console.log('[VitalsMeasurement] ✅ Cámara lista, iniciando medición en 3s...');
      setStatus('ready');
      if (cameraReadyTimerRef.current) clearTimeout(cameraReadyTimerRef.current);
      cameraReadyTimerRef.current = setTimeout(() => {
        cameraReadyTimerRef.current = null;
        startMeasurement();
      }, 3000);
    }

    if (event.state === '_connection_activate_') {
      console.log('[VitalsMeasurement] ✅ Conexión con servidor activada');
    }

    if (event.state === '_connection_close_') {
      if (event.reason === 'normal') {
        console.log('[VitalsMeasurement] ℹ️ Conexión cerrada normalmente (reinicio o detención del SDK):', event);
        return;
      }
      const reasonMessages = {
        'path_not_found': 'Error de conexión: ruta no encontrada',
        'connect_failed': 'Error de conexión: no se pudo conectar al servidor',
        'reject': 'Dominio no autorizado. Contacta a soporte.',
        'unauthorized': 'Error de autorización. Verifica la hora del sistema.'
      };
      const errorMsg = reasonMessages[event.reason] || 'Error de conexión';
      console.error('[VitalsMeasurement] ❌ Conexión cerrada con error:', errorMsg, event);
      clearPendingTimers();
      setError(errorMsg);
      setStatus('error');
    }

    if (event.state === '_restart_') {
      restartAttemptsRef.current += 1;
      const attempt = restartAttemptsRef.current;

      if (attempt > MAX_AUTO_RESTARTS) {
        console.warn(`[VitalsMeasurement] ❌ Máximo de reintentos automáticos alcanzado (${MAX_AUTO_RESTARTS}) para:`, event.reason);
        clearPendingTimers();
        setError(
          'La señal óptica fue inestable tras varios intentos (posible contraluz o movimiento). Ajusta tu iluminación frontal y vuelve a intentarlo.'
        );
        setStatus('error');
        return;
      }

      const restartMessages = {
        'MAX_MEASURE_TIME': `Tiempo máximo excedido. Reintentando (${attempt}/${MAX_AUTO_RESTARTS})...`,
        'FACE_LOSS': `Rostro no detectado. Reintentando (${attempt}/${MAX_AUTO_RESTARTS})...`,
        'ABNORMAL_RESULT': `Resultado anormal. Reintentando (${attempt}/${MAX_AUTO_RESTARTS})...`
      };
      const restartMsg = restartMessages[event.reason] || `Reiniciando medición (${attempt}/${MAX_AUTO_RESTARTS})...`;
      console.warn('[VitalsMeasurement] ⚠️ Restart solicitado por SDK, programando re-inicio:', restartMsg, event);

      setError(restartMsg);
      setProgress(0);
      setScanningStatus('');

      // NOTA CRÍTICA: En fhvitals_sdk.js, cuando el servidor envía `_restart_`,
      // cierra el WebSocket con `_connection_close_` (reason: 'normal') y ejecuta
      // internamente `stopMeasuring()` (`is_measuring = false`). El SDK NO vuelve
      // a invocar `startMeasuring()` por sí solo; debemos relanzarlo explícitamente
      // tras permitir que cierre el socket anterior (~1.5s).
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
      restartTimerRef.current = setTimeout(() => {
        restartTimerRef.current = null;
        if (serviceRef.current) {
          startMeasurement();
        }
      }, 1500);
    }

    if (event.state === '_canvas_size_change_') {
      console.log('[VitalsMeasurement] 📐 Canvas redimensionado:', event);
    }
  };

  const startMeasurement = async () => {
    try {
      if (!serviceRef.current) return;
      console.log('[VitalsMeasurement] 🚀 Iniciando medición...');
      setStatus('measuring');
      setProgress(0);
      setScanningStatus('');

      const measurementParams = {
        age: userInfo.age,
        sex: userInfo.sex,
        height: userInfo.height,
        weight: userInfo.weight,
        bp_mode: 'ternary',
        bp_group: userInfo.bp_group || 'normal'
      };

      console.log('[VitalsMeasurement] 📊 Parámetros:', measurementParams);

      // Reintentar startMeasuring si el rostro no se detecta inmediatamente.
      // ERROR_FACE_NOT_DETECTED ocurre cuando el modelo WASM aún se está
      // compilando o el rostro no está correctamente posicionado todavía.
      const MAX_FACE_RETRIES = 12; // ~12 segundos de tolerancia
      let result;
      for (let attempt = 1; attempt <= MAX_FACE_RETRIES; attempt++) {
        if (!serviceRef.current) return;
        result = await serviceRef.current.startMeasuring(measurementParams);
        const isSuccess = result.error === 0 || result.error === "ERROR_NONE";
        if (isSuccess) {
          console.log(`[VitalsMeasurement] ✅ Medición iniciada en intento ${attempt}`);
          break;
        }
        if (result.error === "ERROR_FACE_NOT_DETECTED") {
          console.log(`[VitalsMeasurement] 🔍 Rostro no detectado, reintentando (${attempt}/${MAX_FACE_RETRIES})...`);
          await new Promise(r => setTimeout(r, 1000));
          continue;
        }
        // Otro error — salir del loop
        break;
      }

      if (!result) return;
      const finalSuccess = result.error === 0 || result.error === "ERROR_NONE";
      if (!finalSuccess) {
        if (result.error === "ERROR_FACE_NOT_DETECTED") {
          throw new Error('No se detectó el rostro. Centra tu cara frente a la cámara con buena iluminación.');
        }
        throw new Error(`Error al iniciar medición: código ${result.error}`);
      }

      console.log('[VitalsMeasurement] ✅ Medición en progreso...');
    } catch (err) {
      console.error('[VitalsMeasurement] ❌ Error al iniciar medición:', err);
      setError(err.message);
      setStatus('error');
    }
  };

  const handleMeasurementComplete = async (result) => {
    console.log('[VitalsMeasurement] 🏁 Finalizando medición...');
    clearPendingTimers();

    // 1. Detener SDK PRIMERO — antes de cualquier cambio de estado que
    //    cause re-render, para que React no intente reconciliar nodos
    //    que el SDK ya eliminó del DOM real.
    try {
      await serviceRef.current.stopMeasuring();
      await serviceRef.current.stopPreview();
      serviceRef.current = null; // evitar doble cleanup en useEffect
    } catch (cleanErr) {
      console.warn('[VitalsMeasurement] Advertencia en cleanup post-medición:', cleanErr);
    }

    // 2. Mapear resultados
    const mappedResults = mapSDKResultToWE360(result);
    console.log('[VitalsMeasurement] 📊 Resultados finales:', mappedResults);

    // 3. Actualizar estado React DESPUÉS de que el SDK limpió el DOM
    //    Mostramos la pantalla de resumen para que el usuario REVISE las
    //    métricas antes de guardarlas. NO llamamos onComplete aquí: el guardado
    //    se dispara recién cuando el usuario confirma con "Usar estos valores".
    setFinalResults(mappedResults);
    setStatus('complete');

    console.log('[VitalsMeasurement] ✅ Medición completada — mostrando resumen para revisión');
  };

  // Confirmación explícita del usuario: guarda y cierra (el padre persiste).
  const handleUseResults = () => {
    if (onComplete && finalResults) {
      onComplete(finalResults);
    }
  };

  // Volver a medir: limpia el SDK actual (si quedó) y reinicia el flujo de
  // inicialización vía el useEffect (depende de retakeCount).
  const handleRetake = async () => {
    clearPendingTimers();
    restartAttemptsRef.current = 0;
    if (serviceRef.current) {
      const currentService = serviceRef.current;
      activeCleanupPromise = currentService.cleanup().catch(() => {});
      try { await activeCleanupPromise; } catch { /* noop */ }
      serviceRef.current = null;
    }
    setFinalResults(null);
    setCurrentResults(null);
    setProgress(0);
    setScanningStatus('');
    setError(null);
    setStatus('initializing');
    setRetakeCount((c) => c + 1);
  };

  const handleCancel = async () => {
    console.log('[VitalsMeasurement] 🚫 Cancelando y limpiando...');
    clearPendingTimers();
    if (serviceRef.current) {
      const currentService = serviceRef.current;
      activeCleanupPromise = currentService.cleanup().catch(() => {});
      try { await activeCleanupPromise; } catch { /* noop */ }
      serviceRef.current = null;
    }
    onClose();
  };

  // Portal aislado FUERA de #root: el SDK de FaceHeart muta el DOM directamente;
  // al montar el modal dentro de #root, ese DOM "sucio" corrompía el árbol global
  // de React y reventaba cualquier render posterior (dashboard, historial) con
  // "insertBefore NotFoundError". Aislándolo en document.body, las mutaciones del
  // SDK quedan en un contenedor aparte y no afectan a #root.
  const portalRef = useRef(null);
  if (portalRef.current === null) {
    portalRef.current = document.createElement('div');
    portalRef.current.setAttribute('data-fh-modal-root', '');
  }
  useEffect(() => {
    const el = portalRef.current;
    document.body.appendChild(el);
    return () => { try { document.body.removeChild(el); } catch { /* noop */ } };
  }, []);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#14B8A6' }}>
              <Camera size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#1E293B' }}>
                Medición de signos vitales
              </h2>
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                Mantén tu rostro en el área marcada (~50 segundos)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            data-testid="vitals-close-btn"
            aria-label="Cerrar medición"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X size={20} style={{ color: '#64748B' }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Canvas Container - Dejar que el SDK controle el tamaño.
              IMPORTANTE: NO se desmonta al completar (eso provoca un conflicto
              React vs DOM del SDK → "insertBefore NotFoundError"). En su lugar
              se OCULTA con CSS, manteniendo el subárbol que el SDK manipuló. */}
          <div
            className="relative bg-black rounded-xl overflow-hidden mb-4 flex items-center justify-center"
            style={{ minHeight: '400px', display: status === 'complete' ? 'none' : 'flex' }}
          >
            <canvas
              ref={canvasRef}
              id="vitals-canvas"
              style={{ 
                maxWidth: '100%',
                maxHeight: '70vh',
                height: 'auto',
                width: 'auto'
              }}
            />

            {/* Overlay de estado */}
            {status === 'initializing' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white z-20">
                <Loader2 size={40} className="animate-spin mb-3" />
                <p className="text-sm font-semibold">Iniciando cámara...</p>
              </div>
            )}

            {status === 'ready' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white z-20">
                <Loader2 size={40} className="animate-spin mb-3 text-teal-400" />
                <p className="text-sm font-semibold">Preparando detección facial...</p>
                <p className="text-xs opacity-75 mt-1">Centra tu rostro frente a la cámara</p>
              </div>
            )}

            {status === 'initializing' && error && error.startsWith('Cargando SDK') && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white z-20">
                <Loader2 size={40} className="animate-spin mb-3 text-teal-400" />
                <p className="text-sm font-semibold text-center px-4">{error}</p>
                <p className="text-xs opacity-60 mt-1">Descargando componentes...</p>
              </div>
            )}

            {status === 'error' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white z-20">
                <AlertCircle size={40} className="mb-3 text-red-400" />
                <p className="text-sm font-semibold text-center px-4">{error}</p>
              </div>
            )}

            {/* Corner Brackets (for targeting/centering) */}
            {(status === 'ready' || status === 'measuring') && (
              <div className="absolute inset-0 pointer-events-none z-10">
                <div className="absolute top-5 left-5 w-6 h-6 border-t-2 border-l-2 border-teal-500/40 rounded-tl-lg" />
                <div className="absolute top-5 right-5 w-6 h-6 border-t-2 border-r-2 border-teal-500/40 rounded-tr-lg" />
                <div className="absolute bottom-5 left-5 w-6 h-6 border-b-2 border-l-2 border-teal-500/40 rounded-bl-lg" />
                <div className="absolute bottom-5 right-5 w-6 h-6 border-b-2 border-r-2 border-teal-500/40 rounded-br-lg" />
              </div>
            )}

            {/* Calming Target (Breathing Center focus circle) */}
            {(status === 'ready' || status === 'measuring') && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="w-56 h-56 border border-white/10 rounded-full flex items-center justify-center">
                  <div className="w-52 h-52 border border-teal-500/25 rounded-full animate-breath flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500/30" />
                  </div>
                </div>
              </div>
            )}

            {/* HUD Overlay - Left Panel (Heart Rate & Blood Pressure) */}
            {status === 'measuring' && currentResults && (
              <div className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 flex-col gap-3 z-10 w-44 pointer-events-none">
                <HUDCard 
                  icon={Heart} 
                  label="Ritmo cardíaco" 
                  value={currentResults.hr} 
                  unit="lpm" 
                  pulseStyle={currentResults.hr ? { animation: `soft-pulse ${60 / currentResults.hr}s infinite ease-in-out` } : undefined}
                />
                <HUDCard 
                  icon={Activity} 
                  label="Presión arterial" 
                  value={currentResults.sbp && currentResults.dbp ? `${currentResults.sbp}/${currentResults.dbp}` : null} 
                  unit="mmHg" 
                />
              </div>
            )}

            {/* HUD Overlay - Right Panel (Oxygen Saturation & Respiration Rate) */}
            {status === 'measuring' && currentResults && (
              <div className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 flex-col gap-3 z-10 w-44 pointer-events-none">
                <HUDCard 
                  icon={Activity} 
                  label="Saturación O₂" 
                  value={currentResults.spo2} 
                  unit="%" 
                />
                <HUDCard 
                  icon={Wind} 
                  label="Frecuencia resp." 
                  value={currentResults.rr} 
                  unit="rpm" 
                />
              </div>
            )}

            {/* Progress Pill */}
            {status === 'measuring' && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-900/85 backdrop-blur-md px-4 py-2.5 rounded-full border border-white/10 flex items-center gap-3 z-10 shadow-lg pointer-events-none transition-all duration-300">
                <span className="text-white text-xs font-medium tracking-wide flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                  Escaneo rPPG {progress}%
                </span>
                <div className="w-20 h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div
                     className="h-full bg-teal-400 rounded-full transition-all duration-300"
                     style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Scanning Status Warning */}
            {scanning_status && status === 'measuring' && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-yellow-500 text-black px-4 py-2 rounded-lg font-semibold text-sm shadow-lg animate-pulse z-20">
                {scanning_status === 'Motion' && '⚠️ Mantente quieto'}
                {scanning_status === 'FaceLoss' && '⚠️ Rostro no detectado'}
              </div>
            )}

            {/* Restart Warning */}
            {error && status === 'measuring' && !scanning_status && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg animate-pulse z-20 flex items-center gap-2">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* HUD Grid for Mobile (shown below the camera on small screens) */}
          {status === 'measuring' && currentResults && (
            <div className="grid grid-cols-2 gap-3 mb-4 md:hidden">
              <HUDCard 
                icon={Heart} 
                label="Ritmo cardíaco" 
                value={currentResults.hr} 
                unit="lpm" 
                pulseStyle={currentResults.hr ? { animation: `soft-pulse ${60 / currentResults.hr}s infinite ease-in-out` } : undefined}
              />
              <HUDCard 
                icon={Activity} 
                label="Saturación O₂" 
                value={currentResults.spo2} 
                unit="%" 
              />
              <HUDCard 
                icon={Activity} 
                label="Presión arterial" 
                value={currentResults.sbp && currentResults.dbp ? `${currentResults.sbp}/${currentResults.dbp}` : null} 
                unit="mmHg" 
              />
              <HUDCard 
                icon={Wind} 
                label="Frecuencia resp." 
                value={currentResults.rr} 
                unit="rpm" 
              />
            </div>
          )}

          {/* Instrucciones */}
          {status === 'measuring' && (
            <div className="rounded-xl p-4 mb-4" style={{ background: "#CCFBF1", border: "1.5px solid #5EEAD4" }}>
              <p className="text-sm font-bold mb-2.5" style={{ color: "#0F4C4C" }}>
                Mantente así durante el escaneo:
              </p>
              <ul className="space-y-2">
                {[
                  "Rostro dentro del área marcada",
                  "Quieto — sin hablar ni moverte",
                  "Iluminación frontal (evita contraluces)",
                  "Ojos abiertos mirando a la cámara",
                ].map((txt, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#134E4A" }}>
                    <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-white text-[9px] font-bold"
                      style={{ background: "#0F766E" }}>✓</span>
                    {txt}
                  </li>
                ))}
              </ul>
            </div>
          )}


          {/* Resultados finales */}
          {status === 'complete' && finalResults && (
            <div className="space-y-4" data-testid="vitals-results-summary">
              <div className="flex items-center gap-2 text-teal-600 mb-1">
                <CheckCircle2 size={24} />
                <span className="font-bold">¡Medición completada!</span>
              </div>
              <p className="text-sm" style={{ color: '#64748B' }}>
                Revisa los valores recolectados. Si los usas, quedarán guardados y podrás
                consultarlos cuando quieras en <strong>Historial de mediciones</strong>.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <ResultCard label="Frecuencia cardíaca" value={finalResults.heartRate} unit="lpm" highlight userAge={userInfo.age} />
                <ResultCard label="Variabilidad (HRV)" value={finalResults.heartRateVariability} unit="ms" highlight userAge={userInfo.age} />
                <ResultCard label="PA Sistólica" value={finalResults.bloodPressureSystolic} unit="mmHg" highlight userAge={userInfo.age} />
                <ResultCard label="PA Diastólica" value={finalResults.bloodPressureDiastolic} unit="mmHg" highlight userAge={userInfo.age} />
                <ResultCard label="Saturación O₂" value={finalResults.oxygenSaturation} unit="%" highlight userAge={userInfo.age} />
                <ResultCard label="Frecuencia resp." value={finalResults.respirationRate} unit="rpm" highlight userAge={userInfo.age} />
                {finalResults.stressIndex != null && (
                  <ResultCard label="Índice de estrés" value={finalResults.stressIndex} unit="/1000" userAge={userInfo.age} />
                )}
                {finalResults.cardiovascularAge != null && (
                  <ResultCard label="Edad cardiovascular" value={finalResults.cardiovascularAge} unit="años" userAge={userInfo.age} />
                )}
                {finalResults.wellnessScore != null && (
                  <ResultCard label="Bienestar" value={finalResults.wellnessScore} unit="/100" userAge={userInfo.age} />
                )}
              </div>

              {/* Nota preventiva de bienestar clínico */}
              <div
                className="p-3.5 rounded-xl bg-amber-50/90 border border-amber-200/80 flex items-start gap-2.5 text-xs text-amber-900 leading-relaxed"
                data-testid="vitals-clinical-disclaimer"
              >
                <Info size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="font-bold">Nota preventiva de bienestar:</strong> Los valores estimados mediante escaneo óptico facial (rPPG) constituyen una aproximación referencial y una alerta preventiva temprana. No sustituyen un diagnóstico médico; ante cualquier parámetro fuera de rango, corrobórelo siempre con un dispositivo clínico validado.
                </div>
              </div>

              <button
                onClick={handleUseResults}
                data-testid="vitals-use-results-btn"
                className="w-full py-3 rounded-lg font-semibold text-white transition-colors"
                style={{ background: '#14B8A6' }}
              >
                Usar estos valores
              </button>
              <button
                onClick={handleRetake}
                data-testid="vitals-retake-btn"
                className="w-full py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                style={{ background: '#F1F5F9', color: '#0F766E' }}
              >
                <Camera size={16} /> Volver a medir
              </button>
              <button
                onClick={handleCancel}
                data-testid="vitals-discard-results-btn"
                className="w-full py-2.5 rounded-lg font-semibold transition-colors border-2"
                style={{ borderColor: '#E2E8F0', color: '#64748B' }}
              >
                Descartar
              </button>
            </div>
          )}

          {/* Botones de acción cuando ocurre un error */}
          {status === 'error' && (
            <div className="space-y-2.5 mt-2">
              <button
                type="button"
                onClick={handleRetake}
                data-testid="vitals-error-retry-btn"
                className="w-full py-3 rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2 cursor-pointer"
                style={{ background: '#14B8A6' }}
              >
                <Camera size={16} /> Volver a intentar
              </button>
              <button
                type="button"
                onClick={handleCancel}
                data-testid="vitals-error-close-btn"
                className="w-full py-2.5 rounded-lg font-semibold transition-colors border-2 cursor-pointer"
                style={{ borderColor: '#E2E8F0', color: '#64748B' }}
              >
                Cerrar / Regresar
              </button>
            </div>
          )}

          {/* Botones durante medición (incluye opción de reintentar manualmente si hay aviso de reinicio) */}
          {status === 'measuring' && (
            <div className="space-y-2">
              {error && (
                <button
                  type="button"
                  onClick={handleRetake}
                  data-testid="vitals-measuring-retry-btn"
                  className="w-full py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: '#F1F5F9', color: '#0F766E' }}
                >
                  <Camera size={16} /> Reiniciar cámara manualmente
                </button>
              )}
              <button
                type="button"
                onClick={handleCancel}
                data-testid="vitals-cancel-measuring-btn"
                className="w-full py-3 rounded-lg font-semibold transition-colors border-2 cursor-pointer"
                style={{ borderColor: '#E2E8F0', color: '#64748B' }}
              >
                Cancelar medición
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  , portalRef.current);
}

const METRIC_EXPLANATIONS = {
  "Frecuencia cardíaca": "Frecuencia con la que late el corazón por minuto (lpm). El rango normal en reposo en adultos es de 60 a 100 lpm.",
  "Presión arterial": "Fuerza que ejerce la sangre contra las paredes arteriales. Se compone de la sistólica (máxima) y la diastólica (mínima). Rango normal: < 120/80 mmHg.",
  "PA Sistólica": "Presión máxima en las arterias durante la contracción del corazón (sístole). Nivel normal: menor a 120 mmHg.",
  "PA Diastólica": "Presión mínima en las arterias cuando el corazón se relaja entre latidos (diástole). Nivel normal: menor a 80 mmHg.",
  "Saturación O₂": "Porcentaje de oxígeno en los glóbulos rojos (%). Valores normales en reposo: 95% o superior.",
  "Frecuencia resp.": "Número de respiraciones por minuto (rpm). El ritmo normal en reposo para adultos es de 12 a 20 rpm.",
  "Edad cardiovascular": "Edad biológica estimada de tu sistema cardiovascular según la elasticidad de los vasos sanguíneos y onda de pulso.",
  "Riesgo CVD": "Porcentaje de riesgo de presentar un evento cardiovascular adverso (como infarto o ACV) en los próximos 10 años.",
  "Riesgo cardiovascular": "Porcentaje de riesgo de presentar un evento cardiovascular adverso en los próximos 10 años.",
  "Bienestar": "Puntuación de bienestar y balance del sistema nervioso autónomo (escala 0-100).",
  "Índice de estrés": "Escala de Baevsky. Mide el estrés regulador del sistema nervioso autónomo. <150 es normal, 150-300 moderado, 300-600 alto, y >600 es crítico."
};

function ResultCard({ label, value, unit, highlight, userAge }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const explanation = METRIC_EXPLANATIONS[label];
  const gauge = getGaugeConfig(label, value, userAge);

  return (
    <div
      className={`p-3 rounded-lg border transition-all relative ${highlight ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200'}`}
    >
      <div className="flex items-center justify-between gap-1 mb-1">
        <p className="text-xs font-medium truncate" style={{ color: highlight ? '#0F766E' : '#64748B' }}>
          {label}
        </p>
        {explanation && (
          <div className="relative shrink-0 flex items-center justify-center">
            <button
              type="button"
              className="text-slate-400 hover:text-teal-600 p-0.5 rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-teal-500/20"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowTooltip(!showTooltip);
              }}
            >
              <Info size={12} />
            </button>
            {showTooltip && (
              <div 
                className="absolute bottom-full right-0 mb-2 w-48 p-2.5 rounded-xl text-[10px] leading-normal text-white bg-slate-900/95 backdrop-blur-md shadow-lg border border-white/10 z-30 pointer-events-none transition-all duration-200"
              >
                {explanation}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-xl font-bold" style={{ color: highlight ? '#0F766E' : '#1E293B' }}>
          {value !== null && value !== undefined ? value : '--'}
        </span>
        {unit && (
          <span className="text-xs" style={{ color: highlight ? '#0F766E' : '#94A3B8' }}>
            {unit}
          </span>
        )}
      </div>

      {/* PageSpeed-style Status Gauge Bar */}
      {value !== null && value !== undefined && gauge && (
        <div className="mt-2 w-full">
          {/* Multi-segment bar */}
          <div className="h-1.5 w-full flex relative bg-slate-100/50 rounded-full">
            {gauge.segments.map((seg, idx) => (
              <div 
                key={idx} 
                style={{ width: seg.width, backgroundColor: seg.color }}
                className={`h-full ${idx === 0 ? 'rounded-l-full' : ''} ${idx === gauge.segments.length - 1 ? 'rounded-r-full' : ''}`}
                title={`${seg.label}: ${seg.range}`}
              />
            ))}
            {/* Indicator Bullet */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border border-white bg-slate-800 shadow-md transition-all duration-500 ease-out z-10"
              style={{ left: `calc(${gauge.markerPosition}% - 5px)` }}
            />
          </div>
          {/* Status Label */}
          <div className="flex justify-between items-center mt-1">
            <span className="text-[8px] font-extrabold uppercase tracking-wider" style={{ color: gauge.statusColor }}>
              {gauge.statusLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper para calcular rangos de semáforo de salud clínica tipo PageSpeed Insights
function getGaugeConfig(label, value, userAge = 35) {
  if (value === null || value === undefined) return null;
  
  // Normalizar valor numérico
  let numVal = parseFloat(value);
  if (isNaN(numVal)) {
    // Si es un rango de presión "SYS/DIA", parsear SYS para el indicador principal
    if (typeof value === 'string' && value.includes('/')) {
      const parts = value.split('/');
      numVal = parseFloat(parts[0]);
    } else {
      return null;
    }
  }

  const COLOR_GREEN = "#10B981";  // Bueno (esmeralda)
  const COLOR_YELLOW = "#F59E0B"; // Regular (ámbar)
  const COLOR_RED = "#EF4444";    // Alerta (rojo)

  let statusLabel = "Bueno";
  let statusColor = COLOR_GREEN;
  let markerPosition = 50; // Porcentaje de 0 a 100
  let segments = [];

  // Mapeador lineal auxiliar para calcular posición del marcador
  const helperMap = (val, inMin, inMax, outMin, outMax) => {
    if (inMax === inMin) return outMin;
    const pct = (val - inMin) / (inMax - inMin);
    return Math.max(outMin, Math.min(outMax, outMin + pct * (outMax - outMin)));
  };

  if (label === "Frecuencia cardíaca") {
    // Bueno: 60-100, Regular: 50-59 o 101-110, Alerta: <50 o >110
    segments = [
      { color: COLOR_YELLOW, width: "20%", label: "Bajo", range: "< 60 lpm" },
      { color: COLOR_GREEN, width: "60%", label: "Normal", range: "60 - 100 lpm" },
      { color: COLOR_RED, width: "20%", label: "Alto", range: "> 100 lpm" }
    ];

    if (numVal < 60) {
      statusLabel = numVal < 50 ? "Alerta (Bajo)" : "Regular (Bajo)";
      statusColor = numVal < 50 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 40, 60, 5, 20);
    } else if (numVal <= 100) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 60, 100, 20, 80);
    } else {
      statusLabel = numVal > 110 ? "Alerta (Alto)" : "Regular (Alto)";
      statusColor = numVal > 110 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 100, 140, 80, 95);
    }
  }
  else if (label === "Presión arterial" || label === "PA Sistólica") {
    // Bueno: <120, Regular: 120-139, Alerta: >=140
    segments = [
      { color: COLOR_GREEN, width: "45%", label: "Bueno", range: "< 120 mmHg" },
      { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "120 - 139 mmHg" },
      { color: COLOR_RED, width: "20%", label: "Alerta", range: ">= 140 mmHg" }
    ];

    if (numVal < 120) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 90, 120, 5, 45);
    } else if (numVal < 140) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 120, 140, 45, 80);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 140, 180, 80, 95);
    }
  }
  else if (label === "PA Diastólica") {
    // Bueno: <80, Regular: 80-89, Alerta: >=90
    segments = [
      { color: COLOR_GREEN, width: "45%", label: "Bueno", range: "< 80 mmHg" },
      { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "80 - 89 mmHg" },
      { color: COLOR_RED, width: "20%", label: "Alerta", range: ">= 90 mmHg" }
    ];

    if (numVal < 80) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 50, 80, 5, 45);
    } else if (numVal < 90) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 80, 90, 45, 80);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 90, 110, 80, 95);
    }
  }
  else if (label === "Saturación O₂") {
    // Bueno: >=95%, Regular: 92-94%, Alerta: <92%
    segments = [
      { color: COLOR_RED, width: "30%", label: "Alerta", range: "< 92%" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "92% - 94%" },
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: ">= 95%" }
    ];

    if (numVal < 92) {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 85, 92, 5, 30);
    } else if (numVal < 95) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 92, 95, 30, 60);
    } else {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 95, 100, 60, 95);
    }
  }
  else if (label === "Frecuencia resp.") {
    // Bueno: 12-20, Regular: 10-11 o 21-24, Alerta: <10 o >24
    segments = [
      { color: COLOR_YELLOW, width: "20%", label: "Bajo", range: "< 12 rpm" },
      { color: COLOR_GREEN, width: "60%", label: "Normal", range: "12 - 20 rpm" },
      { color: COLOR_RED, width: "20%", label: "Alto", range: "> 20 rpm" }
    ];

    if (numVal < 12) {
      statusLabel = numVal < 10 ? "Alerta (Bajo)" : "Regular (Bajo)";
      statusColor = numVal < 10 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 8, 12, 5, 20);
    } else if (numVal <= 20) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 12, 20, 20, 80);
    } else {
      statusLabel = numVal > 24 ? "Alerta (Alto)" : "Regular (Alto)";
      statusColor = numVal > 24 ? COLOR_RED : COLOR_YELLOW;
      markerPosition = helperMap(numVal, 20, 28, 80, 95);
    }
  }
  else if (label === "Variabilidad (HRV)") {
    // Bueno: >50 ms, Regular: 30-50 ms, Alerta: <30 ms
    segments = [
      { color: COLOR_RED, width: "30%", label: "Alerta", range: "< 30 ms" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "30 - 50 ms" },
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "> 50 ms" }
    ];

    if (numVal < 30) {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 10, 30, 5, 30);
    } else if (numVal <= 50) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 30, 50, 30, 60);
    } else {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 50, 110, 60, 95);
    }
  }
  else if (label === "Índice de estrés") {
    // Bueno: <150, Regular: 150-300, Alerta: >=300
    segments = [
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "< 150" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "150 - 300" },
      { color: COLOR_RED, width: "30%", label: "Alerta", range: ">= 300" }
    ];

    if (numVal < 150) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 0, 150, 5, 40);
    } else if (numVal < 300) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 150, 300, 40, 70);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 300, 800, 70, 95);
    }
  }
  else if (label === "Edad cardiovascular") {
    // Relativo a userAge. diff = value - userAge
    // Bueno: diff <= 0, Regular: diff 1-5, Alerta: diff >= 6
    segments = [
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "<= edad real" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "+1 a +5 años" },
      { color: COLOR_RED, width: "30%", label: "Alerta", range: ">= +6 años" }
    ];

    const diff = numVal - userAge;
    if (diff <= 0) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(diff, -10, 0, 5, 40);
    } else if (diff <= 5) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(diff, 0, 5, 40, 70);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(diff, 5, 15, 70, 95);
    }
  }
  else if (label === "Riesgo CVD" || label === "Riesgo cardiovascular") {
    // Bueno: <5%, Regular: 5-10%, Alerta: >10%
    segments = [
      { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "< 5%" },
      { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "5% - 10%" },
      { color: COLOR_RED, width: "30%", label: "Alerta", range: "> 10%" }
    ];

    if (numVal < 5) {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(numVal, 0, 5, 5, 40);
    } else if (numVal <= 10) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(numVal, 5, 10, 40, 70);
    } else {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(numVal, 10, 20, 70, 95);
    }
  }
  else if (label === "Bienestar") {
    // Normalizado a 100. (Bienestar en Step3Clinical viene como /10, por ende numVal es <= 10)
    const val100 = numVal <= 10 ? numVal * 10 : numVal;
    segments = [
      { color: COLOR_RED, width: "35%", label: "Alerta", range: "< 50%" },
      { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "50% - 79%" },
      { color: COLOR_GREEN, width: "30%", label: "Bueno", range: ">= 80%" }
    ];

    if (val100 < 50) {
      statusLabel = "Alerta";
      statusColor = COLOR_RED;
      markerPosition = helperMap(val100, 0, 50, 5, 35);
    } else if (val100 < 80) {
      statusLabel = "Regular";
      statusColor = COLOR_YELLOW;
      markerPosition = helperMap(val100, 50, 80, 35, 70);
    } else {
      statusLabel = "Bueno";
      statusColor = COLOR_GREEN;
      markerPosition = helperMap(val100, 80, 100, 70, 95);
    }
  } else {
    return null;
  }

  return {
    statusLabel,
    statusColor,
    markerPosition,
    segments
  };
}

function HUDCard({ icon: Icon, label, value, unit, pulseStyle }) {
  return (
    <div className="bg-white/85 backdrop-blur-md border border-white/50 rounded-xl p-3 shadow-sm flex flex-col gap-1 w-full min-w-[140px] pointer-events-none transition-all duration-300">
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={14} className="text-teal-600/70 shrink-0" />}
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500/80 truncate">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1 mt-0.5 justify-between">
        <div className="flex items-baseline gap-0.5">
          <span className="text-lg font-extrabold text-slate-800 tabular-nums">
            {value ?? '--'}
          </span>
          {unit && (
            <span className="text-[10px] font-bold text-slate-400">
              {unit}
            </span>
          )}
        </div>
        {pulseStyle && (
          <span 
            className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0 shadow-sm"
            style={pulseStyle}
          />
        )}
      </div>
    </div>
  );
}

